import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin, adminAxios } from "./AdminContext";
import { CheckCircle2, AlertCircle, KeyRound, UserCog } from "lucide-react";

export default function AccountSettings() {
  const { token, user, logout } = useAdmin();
  const navigate = useNavigate();

  const handlePostChangeLogout = (msg) => {
    setTimeout(() => { logout(); navigate("/admin/login", { replace: true }); }, 1500);
  };

  return (
    <div className="max-w-xl space-y-8" data-testid="admin-account-settings">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3"><KeyRound size={26} /> Hesap Ayarları</h1>
        <p className="text-white/50 mt-2">Giriş yapan kullanıcı: <span className="text-white font-semibold">{user?.username}</span></p>
      </div>

      <UsernameForm token={token} onDone={handlePostChangeLogout} />
      <PasswordForm token={token} onDone={handlePostChangeLogout} />

      <style>{`.input-dark-admin{width:100%;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.12);border-radius:12px;padding:12px 14px;color:#fff;font-size:14px;outline:none;transition:.2s}.input-dark-admin:focus{border-color:#FF3B30;background:rgba(255,255,255,.08)}`}</style>
    </div>
  );
}

function UsernameForm({ token, onDone }) {
  const [form, setForm] = useState({ current_password: "", new_username: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    if (!/^[A-Za-z0-9._-]{3,64}$/.test(form.new_username)) {
      setStatus({ state: "error", msg: "Kullanıcı adı 3-64 karakter olmalı; harf/rakam/.-_ kullanılabilir." });
      return;
    }
    try {
      const res = await adminAxios(token).post("/admin/change-username", form);
      setStatus({ state: "success", msg: res.data?.message || "Kullanıcı adı güncellendi." });
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Hata oluştu." });
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 space-y-5" data-testid="admin-change-username-form">
      <div>
        <h2 className="font-display text-lg font-bold text-white flex items-center gap-2"><UserCog size={18} /> Kullanıcı Adı</h2>
        <p className="text-xs text-white/50 mt-1">Değişiklik sonrası otomatik çıkış yapılır, yeni kullanıcı adıyla tekrar giriş yapın.</p>
      </div>
      <Field label="Yeni Kullanıcı Adı" testId="new-username">
        <input required value={form.new_username}
          onChange={(e) => setForm({ ...form, new_username: e.target.value })}
          className="input-dark-admin" placeholder="örn: onurkarpuz" autoComplete="off" />
      </Field>
      <Field label="Mevcut Şifre (onay)" testId="username-current-password">
        <input type="password" required value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          className="input-dark-admin" autoComplete="current-password" />
      </Field>

      {status.state === "success" && (
        <div className="flex items-start gap-2 rounded-xl bg-[#34C759]/10 border border-[#34C759]/30 p-3 text-sm text-[#4ADE80]" data-testid="admin-change-username-success">
          <CheckCircle2 size={16} className="mt-0.5" />{status.msg}
        </div>
      )}
      {status.state === "error" && (
        <div className="flex items-start gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3 text-sm text-[#FF6B63]" data-testid="admin-change-username-error">
          <AlertCircle size={16} className="mt-0.5" />{status.msg}
        </div>
      )}

      <button type="submit" disabled={status.state === "loading"}
        data-testid="admin-change-username-submit"
        className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-3 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60">
        {status.state === "loading" ? "Kaydediliyor..." : "Kullanıcı Adını Değiştir"}
      </button>
    </form>
  );
}

function PasswordForm({ token, onDone }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", new_password_confirm: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    if (form.new_password !== form.new_password_confirm) {
      setStatus({ state: "error", msg: "Yeni şifreler eşleşmiyor." }); return;
    }
    if (form.new_password.length < 8) {
      setStatus({ state: "error", msg: "Yeni şifre en az 8 karakter olmalı." }); return;
    }
    try {
      await adminAxios(token).post("/admin/change-password", form);
      setStatus({ state: "success", msg: "Şifre güncellendi. Çıkış yapılıyor..." });
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Hata oluştu." });
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 md:p-8 space-y-5" data-testid="admin-change-password-form">
      <div>
        <h2 className="font-display text-lg font-bold text-white flex items-center gap-2"><KeyRound size={18} /> Şifre</h2>
        <p className="text-xs text-white/50 mt-1">En az 8 karakter. Değişiklik sonrası otomatik çıkış yapılır.</p>
      </div>
      <Field label="Mevcut Şifre" testId="current-password">
        <input type="password" required value={form.current_password}
          onChange={(e) => setForm({ ...form, current_password: e.target.value })}
          className="input-dark-admin" autoComplete="current-password" />
      </Field>
      <Field label="Yeni Şifre" testId="new-password">
        <input type="password" required minLength={8} value={form.new_password}
          onChange={(e) => setForm({ ...form, new_password: e.target.value })}
          className="input-dark-admin" autoComplete="new-password" />
      </Field>
      <Field label="Yeni Şifre (Tekrar)" testId="new-password-confirm">
        <input type="password" required minLength={8} value={form.new_password_confirm}
          onChange={(e) => setForm({ ...form, new_password_confirm: e.target.value })}
          className="input-dark-admin" autoComplete="new-password" />
      </Field>

      {status.state === "success" && (
        <div className="flex items-start gap-2 rounded-xl bg-[#34C759]/10 border border-[#34C759]/30 p-3 text-sm text-[#4ADE80]" data-testid="admin-change-password-success">
          <CheckCircle2 size={16} className="mt-0.5" />{status.msg}
        </div>
      )}
      {status.state === "error" && (
        <div className="flex items-start gap-2 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3 text-sm text-[#FF6B63]" data-testid="admin-change-password-error">
          <AlertCircle size={16} className="mt-0.5" />{status.msg}
        </div>
      )}

      <button type="submit" disabled={status.state === "loading"}
        data-testid="admin-change-password-submit"
        className="inline-flex items-center gap-2 rounded-full bg-[#FF3B30] text-white px-7 py-3 font-semibold hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60">
        {status.state === "loading" ? "Kaydediliyor..." : "Şifreyi Değiştir"}
      </button>
    </form>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block" data-testid={testId}>
      <span className="block text-xs font-bold tracking-[0.2em] uppercase text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}
